import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import * as Sentry from '@sentry/react'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { APP_VERSION } from './version'
import App from './App'
import { i18nReady } from './i18n' // Initialisation i18n AVANT le rendu
import 'react-day-picker/style.css' // Base calendar styles
import './index.css' // Custom Argalis styles (overrides)

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 30_000,
            gcTime: 5 * 60_000,
            retry: 1,
            refetchOnWindowFocus: false,
            // 🔴 Hors ligne DÉCLARÉ (Wi-Fi coupé, mode avion), le défaut `'online'` met la
            // lecture EN PAUSE : ni chargement, ni échec — l'écran affirmait son état VIDE
            // (« Aucun échange consigné »). `'always'` laisse `fetch` échouer : l'écran dit
            // « Erreur réseau ».
            networkMode: 'always',
            // ⚠️ EXPLICITE : sous `'always'`, React Query 5 éteint ce réglage par défaut
            // (`defaultQueryOptions`). Sans lui, une liste en échec ne se relirait plus
            // d'elle-même au retour du réseau — seul rafraîchissement automatique ici.
            refetchOnReconnect: true,
        },
        // Même règle pour un geste : en pause, il ne se résolvait ni ne se rejetait — pas un mot.
        mutations: { networkMode: 'always' },
    },
})

// Sentry — désactivé si VITE_SENTRY_DSN est vide (dev local)
if (import.meta.env.VITE_SENTRY_DSN) {
    Sentry.init({
        dsn: import.meta.env.VITE_SENTRY_DSN,
        environment: import.meta.env.VITE_ENVIRONMENT || 'development',
        release: import.meta.env.VITE_APP_VERSION || APP_VERSION,
        tracesSampleRate: 0.0, // Désactivé — plan gratuit
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: 1.0, // Replay uniquement sur erreur
        beforeSend(event) {
            // GDPR : ne pas envoyer les bodies de requêtes
            if (event.breadcrumbs) {
                event.breadcrumbs = event.breadcrumbs.map(b => {
                    if (b.category === 'fetch' || b.category === 'xhr') {
                        delete b.data?.request_body
                    }
                    return b
                })
            }
            return event
        },
        ignoreErrors: [
            'ResizeObserver loop',
            'Network request failed',
            'Load failed',
            'Failed to fetch',
            'AbortError',
        ],
        integrations: [
            Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true }),
        ],
    })
}

// Service Worker — registration + auto-update transparent
// Le reload se produit pendant le loading spinner de checkAuth(), invisible pour l'utilisateur.
if ('serviceWorker' in navigator && import.meta.env.PROD) {
    navigator.serviceWorker.register('/sw.js').then((registration) => {
        // Vérifier les mises à jour à chaque lancement
        registration.update()
    })

    // Quand un nouveau SW prend le contrôle → reload pour charger le nouveau code
    navigator.serviceWorker.addEventListener('controllerchange', () => {
        window.location.reload()
    })
}

function renderApp() {
    ReactDOM.createRoot(document.getElementById('root')!).render(
        <React.StrictMode>
            <QueryClientProvider client={queryClient}>
                <ErrorBoundary>
                    <BrowserRouter>
                        <App />
                    </BrowserRouter>
                </ErrorBoundary>
            </QueryClientProvider>
        </React.StrictMode>,
    )
}

// Attendre que la langue DÉTECTÉE soit chargée avant le premier rendu.
// Pour un francophone le dictionnaire est bundlé : la promesse est déjà tenue,
// le coût est une micro-tâche. Pour un anglophone, cela évite d'afficher toute
// l'interface en français avant de la voir basculer (l'anglais est chargé à la
// demande depuis le 2026-08-12 — cf. src/i18n.ts).
// Le `catch` est indispensable : un dictionnaire injoignable ne doit pas laisser
// une page BLANCHE — mieux vaut l'interface en langue de repli.
i18nReady.catch(() => {}).finally(renderApp)
